import { useEffect, useState } from 'react'
import {
  Modal,
  ModalDialog,
  DialogTitle,
  DialogContent,
  Stack,
  FormControl,
  FormLabel,
  FormHelperText,
  Input,
  Textarea,
  Button,
  Typography,
  CircularProgress,
} from '@mui/joy'
import { useQuery } from '@tanstack/react-query'
import toast from 'react-hot-toast'
import { documentEmailApi } from '../../lib/billingApi'

export type DocumentEmailKind = 'invoice' | 'estimate'

interface SendDocumentEmailModalProps {
  open: boolean
  kind: DocumentEmailKind
  documentId: number | null
  onClose: () => void
  onSent?: () => void
}

const LABELS: Record<DocumentEmailKind, { title: string; sent: string }> = {
  invoice: { title: 'Send Invoice', sent: 'Invoice sent' },
  estimate: { title: 'Send Estimate', sent: 'Estimate sent' },
}

/**
 * Preview and edit the email that carries an invoice or estimate before it goes
 * out. The draft comes from the backend (Billing Settings wording, with a
 * built-in fallback) and the branded PDF is attached server-side.
 *
 * Deliberately has no email-template picker: the older flow required an
 * EmailTemplate row that no organization is created with, which left Send
 * permanently disabled.
 */
export default function SendDocumentEmailModal({
  open,
  kind,
  documentId,
  onClose,
  onSent,
}: SendDocumentEmailModalProps) {
  const [toEmail, setToEmail] = useState('')
  const [subject, setSubject] = useState('')
  const [message, setMessage] = useState('')
  const [sending, setSending] = useState(false)

  const { data: draft, isLoading } = useQuery({
    queryKey: ['document-email-draft', kind, documentId],
    queryFn: () => documentEmailApi.getDraft(kind, documentId as number),
    enabled: open && documentId != null,
  })

  useEffect(() => {
    if (draft) {
      setToEmail(draft.to_email || '')
      setSubject(draft.subject || '')
      setMessage(draft.message || '')
    }
  }, [draft])

  const handleSend = async () => {
    if (documentId == null) return
    if (!toEmail.trim()) {
      toast.error('Please enter a recipient email')
      return
    }
    setSending(true)
    try {
      const res = await documentEmailApi.send(kind, documentId, {
        to_email: toEmail.trim(),
        subject,
        message,
      })
      toast.success(res?.message || LABELS[kind].sent)
      onSent?.()
      onClose()
    } catch (e) {
      toast.error(e instanceof Error ? e.message : `Failed to send ${kind}`)
    } finally {
      setSending(false)
    }
  }

  return (
    <Modal open={open} onClose={() => (!sending ? onClose() : undefined)}>
      <ModalDialog sx={{ maxWidth: 560, width: '100%' }}>
        <DialogTitle>{LABELS[kind].title}</DialogTitle>
        <DialogContent sx={{ overflow: 'visible' }}>
          {isLoading ? (
            <Stack alignItems="center" sx={{ py: 4 }}>
              <CircularProgress size="sm" />
            </Stack>
          ) : (
            <Stack spacing={2} sx={{ mt: 1 }}>
              <FormControl required>
                <FormLabel>To</FormLabel>
                <Input
                  type="email"
                  value={toEmail}
                  onChange={(e) => setToEmail(e.target.value)}
                  placeholder="customer@example.com"
                />
              </FormControl>

              <FormControl>
                <FormLabel>Subject</FormLabel>
                <Input value={subject} onChange={(e) => setSubject(e.target.value)} />
              </FormControl>

              <FormControl>
                <FormLabel>Message</FormLabel>
                <Textarea
                  minRows={8}
                  value={message}
                  onChange={(e) => setMessage(e.target.value)}
                />
                <FormHelperText>
                  Line breaks are preserved. Edit the default wording in Billing Settings.
                </FormHelperText>
              </FormControl>

              <Typography level="body-sm" textColor="text.tertiary">
                The {kind} PDF is attached automatically.
              </Typography>

              <Stack direction="row" spacing={1} justifyContent="flex-end" sx={{ pt: 1 }}>
                <Button variant="plain" color="neutral" onClick={onClose} disabled={sending}>
                  Cancel
                </Button>
                <Button onClick={handleSend} loading={sending}>
                  Send
                </Button>
              </Stack>
            </Stack>
          )}
        </DialogContent>
      </ModalDialog>
    </Modal>
  )
}
